import { RelatedEntityType } from "@dune/types";
import { getDocumentMappingKey } from "@dune/utils";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
	useCreateEmailTemplate,
	useUpdateEmailTemplate,
} from "../../../../api/mutations/email-templates.mutations";
import {
	useGetEmailTemplate,
	useGetEmailTemplateMappings,
} from "../../../../api/queries/email-templates.queries";
import { useImageUpload } from "../../../../hooks/useImageUpload";
import { extractPlaceholders } from "../../../../utils/template.utils";
import { getEntityLabel } from "./constants";
import { AvailableField, EmailTemplateFormData } from "./types";

interface UseEmailTemplateEditorProps {
	id?: string;
	copyFromId?: string;
	initialName?: string;
}

export const useEmailTemplateEditor = ({
	id,
	copyFromId,
	initialName,
}: UseEmailTemplateEditorProps) => {
	const isEditing = Boolean(id);
	const isCopying = Boolean(copyFromId);
	const { uploadImage } = useImageUpload();

	const {
		data: template,
		isLoading: isLoadingTemplate,
		error: templateError,
	} = useGetEmailTemplate(id || "");

	const { data: copyTemplate, isLoading: isLoadingCopyTemplate } =
		useGetEmailTemplate(copyFromId || "");

	const {
		data: mappings,
		isLoading: isLoadingMappings,
		error: mappingsError,
	} = useGetEmailTemplateMappings();

	const createTemplate = useCreateEmailTemplate();
	const updateTemplate = useUpdateEmailTemplate();

	const form = useForm<EmailTemplateFormData>({
		defaultValues: {
			name: "",
			subject: "",
			htmlContent: "",
			entityModel: RelatedEntityType.BROKER,
		},
	});

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = form;
	const watchedValues = watch();

	useEffect(() => {
		if (isCopying && copyTemplate) {
			setValue("name", initialName || `${copyTemplate.name} (Copie)`);
			setValue("subject", copyTemplate.subject);
			setValue("htmlContent", copyTemplate.htmlContent);
			setValue("entityModel", copyTemplate.entityModel);
			setValue("labels", copyTemplate.labels);
		} else if (isEditing && template) {
			setValue("name", template.name);
			setValue("subject", template.subject);
			setValue("htmlContent", template.htmlContent);
			setValue("entityModel", template.entityModel);
			setValue("labels", template.labels);
		}
	}, [template, copyTemplate, isEditing, isCopying, setValue, initialName]);

	const handleCopyPlaceholder = (placeholder: string) => {
		navigator.clipboard.writeText(`{{${placeholder}}}`);
		toast.success(`Copié : {{${placeholder}}}`);
	};

	const availableFields: AvailableField[] = useMemo(() => {
		const entityModel =
			(isEditing && template?.entityModel) ||
			watchedValues.entityModel ||
			RelatedEntityType.BROKER;

		if (!mappings || !entityModel) return [];

		const mappingKey = getDocumentMappingKey(entityModel);
		const entityMapping = mappings.documentMappings[mappingKey];
		if (!entityMapping) return [];

		const fields: AvailableField[] = [];

		let placeholderPrefix: string;
		if (entityModel === RelatedEntityType.BROKER) {
			placeholderPrefix = "society";
		} else {
			placeholderPrefix = entityModel.toLowerCase();
		}

		for (const field of entityMapping.fields) {
			const fieldKey = `${placeholderPrefix}.${field}`;
			const label = entityMapping.labels?.[field] || field;

			let category = getEntityLabel(entityModel);
			if (field.includes("address.")) {
				category = `${getEntityLabel(entityModel)} - Adresse`;
			} else if (field.includes("signataire.") || field.includes("referent.")) {
				category = `${getEntityLabel(entityModel)} - Contacts`;
			} else if (
				field.includes("Amount") ||
				field.includes("prime") ||
				field.includes("remuneration")
			) {
				category = `${getEntityLabel(entityModel)} - Financier`;
			} else if (field.includes("Date") || field.includes("At")) {
				category = `${getEntityLabel(entityModel)} - Dates`;
			}

			fields.push({ key: fieldKey, label, category });
		}

		if (mappings.computedFields?.[mappingKey]) {
			for (const [fieldName, config] of Object.entries(
				mappings.computedFields[mappingKey],
			)) {
				const fieldKey = `${placeholderPrefix}.${fieldName}`;
				const label = config.label || fieldName;
				const category = `${getEntityLabel(entityModel)} - Calculés`;

				fields.push({ key: fieldKey, label, category });
			}
		}

		if (mappings.relations?.[mappingKey]) {
			for (const [relationName, relationConfig] of Object.entries(
				mappings.relations[mappingKey],
			)) {
				const config = relationConfig as { model: string; label: string };
				// Find the related entity mapping
				const relatedEntityKey = Object.entries(mappings.documentMappings).find(
					([_, mapping]) => mapping.prismaModel === config.model,
				)?.[0];

				if (relatedEntityKey) {
					const relatedMapping = mappings.documentMappings[relatedEntityKey];
					const relationFields = relatedMapping?.fields || [];

					for (const relatedField of relationFields) {
						const fieldKey = `${relationName}.${relatedField}`;
						const label = `${config.label} - ${relatedMapping?.labels?.[relatedField] || relatedField}`;
						const category = `${getEntityLabel(entityModel)} - Relations`;

						fields.push({ key: fieldKey, label, category });
					}
				}
			}
		}

		if (mappings.tableDataSources?.[mappingKey]) {
			for (const [tableName, tableConfig] of Object.entries(
				mappings.tableDataSources[mappingKey],
			)) {
				const tableKey = `${placeholderPrefix}.${tableName}`;
				const tableLabel = tableConfig.label;
				const tableCategory = `${getEntityLabel(entityModel)} - Tableaux`;

				fields.push({
					key: tableKey,
					label: tableLabel,
					category: tableCategory,
				});

				if (tableConfig.model !== "string") {
					const relatedEntityKey = Object.entries(
						mappings.documentMappings,
					).find(
						([_, mapping]) => mapping.prismaModel === tableConfig.model,
					)?.[0];

					if (relatedEntityKey) {
						const relatedMapping = mappings.documentMappings[relatedEntityKey];
						const tableFields = relatedMapping?.fields || [];

						for (const field of tableFields) {
							const fieldKey = `${placeholderPrefix}.${tableName}.${field}`;
							const label = `${tableConfig.label} - ${relatedMapping?.labels?.[field] || field}`;
							const category = `${getEntityLabel(entityModel)} - Champs de tableau`;

							fields.push({ key: fieldKey, label, category });
						}
					}
				} else {
					const valueKey = `${placeholderPrefix}.${tableName}.value`;
					const indexKey = `${placeholderPrefix}.${tableName}.index`;

					fields.push({
						key: valueKey,
						label: `${tableConfig.label} - Valeur`,
						category: `${getEntityLabel(entityModel)} - Champs de tableau`,
					});
					fields.push({
						key: indexKey,
						label: `${tableConfig.label} - Index`,
						category: `${getEntityLabel(entityModel)} - Champs de tableau`,
					});
				}
			}
		}

		if (mappings.staticTags) {
			for (const tag of mappings.staticTags) {
				const fieldKey = tag.key;
				const label = tag.label;
				const category = "Tags statiques";

				fields.push({ key: fieldKey, label, category });
			}
		}

		if (mappings.parameterizedTags) {
			for (const [key, tag] of Object.entries(mappings.parameterizedTags)) {
				const fieldKey = key;
				const label = `${tag.label} (${tag.description})`;
				const category = "Paramètres globaux";

				fields.push({ key: fieldKey, label, category });
			}
		}

		return fields;
	}, [mappings, watchedValues.entityModel, template?.entityModel, isEditing]);

	const invalidPlaceholders = useMemo(() => {
		const entityModel =
			(isEditing && template?.entityModel) ||
			watchedValues.entityModel ||
			RelatedEntityType.BROKER;

		if (!mappings || !entityModel) return [];

		const allText = `${watchedValues.subject || ""} ${watchedValues.htmlContent || ""}`;
		const detectedPlaceholders = extractPlaceholders(allText);

		if (detectedPlaceholders.length === 0) return [];

		const mappingKey = getDocumentMappingKey(entityModel);
		const entityMapping = mappings.documentMappings[mappingKey];
		if (!entityMapping) return detectedPlaceholders;

		// Use the same prefix logic as above
		let placeholderPrefix: string;
		if (entityModel === RelatedEntityType.BROKER) {
			placeholderPrefix = "society";
		} else {
			placeholderPrefix = entityModel.toLowerCase();
		}

		const entityPrefix = `${placeholderPrefix}.`;
		const staticTagKeys = mappings.staticTags?.map((tag) => tag.key) || [];
		const globalParamKeys = Object.keys(mappings.parameterizedTags || {});

		const invalidFields = detectedPlaceholders.filter((placeholder) => {
			// 1. Check static tags (no dot, standalone)
			if (!placeholder.includes(".") && staticTagKeys.includes(placeholder)) {
				return false; // Valid static tag
			}

			// 2. Check global parameters (format: param.field)
			const parts = placeholder.split(".");
			if (parts.length === 2 && globalParamKeys.includes(placeholder)) {
				return false; // Valid global parameter
			}

			// 3. Check entity fields (format: entity.field or relation.field)
			if (placeholder.startsWith(entityPrefix)) {
				const fieldPath = placeholder.substring(entityPrefix.length);

				if (entityMapping.fields.includes(fieldPath)) {
					return false;
				}

				if (
					mappings.computedFields?.[mappingKey] &&
					Object.keys(mappings.computedFields[mappingKey]).includes(fieldPath)
				) {
					return false;
				}

				if (mappings.tableDataSources?.[mappingKey]) {
					for (const [tableName, tableConfig] of Object.entries(
						mappings.tableDataSources[mappingKey],
					)) {
						if (fieldPath === tableName) {
							return false;
						}

						if (fieldPath.startsWith(`${tableName}.`)) {
							const tableFieldPath = fieldPath.substring(
								`${tableName}.`.length,
							);

							if (tableConfig.model === "string") {
								if (["value", "index"].includes(tableFieldPath)) {
									return false;
								}
							} else {
								if (
									tableConfig.computedFields &&
									Object.keys(tableConfig.computedFields).includes(
										tableFieldPath,
									)
								) {
									return false;
								}

								const relatedEntityKey = Object.entries(
									mappings.documentMappings,
								).find(
									([_, mapping]) => mapping.prismaModel === tableConfig.model,
								)?.[0];

								if (relatedEntityKey) {
									const relatedMapping =
										mappings.documentMappings[relatedEntityKey];
									if (relatedMapping?.fields.includes(tableFieldPath)) {
										return false;
									}
								}
							}
						}
					}
				}

				return true;
			}

			if (mappings.relations?.[mappingKey]) {
				for (const [relationName, relationConfig] of Object.entries(
					mappings.relations[mappingKey],
				)) {
					const config = relationConfig as { model: string; label: string };
					if (placeholder.startsWith(`${relationName}.`)) {
						const relationFieldPath = placeholder.substring(
							`${relationName}.`.length,
						);

						const relatedEntityKey = Object.entries(
							mappings.documentMappings,
						).find(([_, mapping]) => mapping.prismaModel === config.model)?.[0];

						if (relatedEntityKey) {
							const relatedMapping =
								mappings.documentMappings[relatedEntityKey];
							if (relatedMapping?.fields.includes(relationFieldPath)) {
								return false;
							}
						}
					}
				}
			}

			if (
				entityModel === RelatedEntityType.BROKER &&
				placeholder.startsWith("broker.")
			) {
				return true;
			}

			return true;
		});

		return invalidFields;
	}, [
		mappings,
		watchedValues.entityModel,
		watchedValues.subject,
		watchedValues.htmlContent,
		template?.entityModel,
		isEditing,
	]);

	return {
		form,
		control,
		errors,
		handleSubmit,
		watchedValues,

		isLoading:
			isLoadingTemplate ||
			isLoadingMappings ||
			(isCopying && isLoadingCopyTemplate),
		error: templateError || mappingsError,

		template,
		copyTemplate,
		isEditing,
		isCopying,

		createTemplate,
		updateTemplate,

		availableFields,
		handleCopyPlaceholder,
		invalidPlaceholders,

		uploadImage,
	};
};
